import React, { ChangeEvent, FunctionComponent, useContext, useEffect, useState } from 'react';
import { InfoOutlined } from '@mui/icons-material'
import { Box, Chip, FormControl, FormControlLabel, FormGroup, SelectChangeEvent, Stack, Switch, Typography, useMediaQuery } from '@mui/material'
import { useNavigate } from 'react-router-dom';
import LocationService from '../../../shared/services/location/location.service';
import { ICountry } from '../../../shared/services/location/location.model';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IOrganisationDetails } from '../../../shared/services/organisation/organisation.model';
import { IGroup } from '../../../shared/services/group/group.model';
import OrganisationService from '../../../shared/services/organisation/organisation.service';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import gaiaTheme from '../../../root-components/theme';
import { getDescription } from '../../../shared/data/group-tooltip.data';
import { formatGroupLabel, sortGroupsByGhgCategory } from '../../../shared/constants/ghg-category';
import { TooltipHover } from '../miscellaneous-components/tooltip-hover';
import UserDetailsService from '../../../shared/services/user-details/user-details.service';
import { IUserDetails } from '../../../shared/services/user-details/user-details.model';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model';
import { FormColorInputDisplay } from '../form-field-components/new-and-edit/form-color-input';
import { FormSelectInputDisplay } from '../form-field-components/new-and-edit/form-select-input';
import { FormTextInputDisplay } from '../form-field-components/new-and-edit/form-text-input';
import { FormToggleInputDisplay } from '../form-field-components/new-and-edit/form-toggle-input';
import { DistributionLossCorrectionDialog } from '../../dialogs/settings/distribution-loss-correction-dialog'
import { WttPolicyDialog } from '../../dialogs/settings/wtt-policy-dialog'
import { LedgerCoverageCorrectionDialog } from '../../dialogs/settings/ledger-coverage-correction-dialog'
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';
import { FormAutoCompleteInputDisplay } from '../form-field-components/new-and-edit/form-autocomplete-input';
import { FormRadioInputDisplay } from '../form-field-components/new-and-edit/form-radio-input';

export const OverviewEditForm: FunctionComponent<{
  updatedOrganisationDetails: IOrganisationDetails,
  organisationDetails: IOrganisationDetails,
  submitOrganisationHandler: (postData: IOrganisationDetails) => void,
  editField: string,
  setEditField: React.Dispatch<React.SetStateAction<any>>,
  setGroups: React.Dispatch<React.SetStateAction<any>>,
  groups: IGroup[],
}> = ({
  updatedOrganisationDetails,
  submitOrganisationHandler: submitOrganisationDetails,
  organisationDetails,
  editField,
  setEditField,
  groups,
  setGroups,
}) => {

    // Every save on this form sends the whole form, so includeWtt is taken out of all of them: a
    // page loaded before someone else switched well-to-tank would otherwise switch it back without
    // the confirmation. The well-to-tank switch saves through applyIncludeWtt instead.
    const submitOrganisationHandler = (postData: IOrganisationDetails) => {
      const { includeWtt: _omitted, ...rest } = postData
      submitOrganisationDetails(rest as IOrganisationDetails)
    }

    const userDetailsContext = useContext(UserDetailsContext)
    const userDetails = userDetailsContext.userDetails
    const userNotificationsContext = useContext(UserNotificationsContext)
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const organisationService = new OrganisationService()
    const userDetailsService = new UserDetailsService();
    const locationService = LocationService.getInstance();
    const subsidiaryService = SubsidiaryService.getInstance()
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>([]);
    const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
    const [ledgerBillsDialogOpen, setLedgerBillsDialogOpen] = useState(false);
    // The well-to-tank setting the user is switching to, while the confirmation is open.
    const [wttDialogTarget, setWttDialogTarget] = useState<boolean | null>(null);
    const [countries, setCountries] = useState<ICountry[]>();
    const [users, setUsers] = React.useState<IUserDetails[]>([]);
    // const [openDate, setOpenDate] = useState(false);
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
    const navigate = useNavigate();


    useEffect(() => {
      locationService.getAllCountries()
        .then(response => {
          setCountries(response)
        })
        .catch(err => {
          console.log(err)
        })

      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response)
        })
        .catch(err => {
          console.log(err)
        })

      if (userDetails?.organisation.name === null) {
        setEditField('name')
        formik.submitForm()
      }

      userDetailsService.getAllUserDetails(`?status=active`)
        .then((response) => {
          setUsers(response.filter(user => !(user.inviteSent === true && user.inviteAccepted === false)))
        })
        .catch(err => {
          console.log(err)
        })
    }, [])

    const overviewSchema = Yup.object().shape({
      name: Yup.string().required('Company name is required'),
      sector: Yup.string().nullable(),
      companyType: Yup.string().nullable(),
      countryId: Yup.string().nullable(),
      accountingYearEndDate: Yup.string().nullable(),
      assignedUserId: Yup.string().nullable(),
      enableProductCarbonFootprint: Yup.boolean(),
      enableSubsidiaries: Yup.boolean(),
      enableProjects: Yup.boolean(),
      primaryColor: Yup.string(),
      enableRecs: Yup.boolean(),
      currencyCode: Yup.string().required('Organisation currency is required'),
      includeWtt: Yup.boolean(),
      includeTransmissionDistributionLosses: Yup.boolean(),
      includeLedgerBills: Yup.boolean(),
      defaultMarketBased: Yup.boolean(),
      isFlagEnabled: Yup.boolean(),
      inflationAdjustmentEnabled: Yup.boolean(),
    })

    const formik = useFormik({
      initialValues: {
        name: updatedOrganisationDetails.name,
        sector: updatedOrganisationDetails.sector,
        companyType: updatedOrganisationDetails.companyType ?? '',
        countryId: updatedOrganisationDetails.countryId,
        accountingYearEndDate: updatedOrganisationDetails.accountingYearEndDate,
        assignedUserId: updatedOrganisationDetails.assignedUserId,
        enableProductCarbonFootprint: updatedOrganisationDetails.enableProductCarbonFootprint,
        enableSubsidiaries: updatedOrganisationDetails.enableSubsidiaries,
        enableProjects: updatedOrganisationDetails.enableProjects,
        primaryColor: updatedOrganisationDetails.primaryColor,
        enableRecs: updatedOrganisationDetails.enableRecs,
        currencyCode: updatedOrganisationDetails.currencyCode,
        includeWtt: updatedOrganisationDetails.includeWtt,
        includeTransmissionDistributionLosses: updatedOrganisationDetails.includeTransmissionDistributionLosses ?? false,
        includeLedgerBills: updatedOrganisationDetails.includeLedgerBills ?? false,
        defaultMarketBased: updatedOrganisationDetails.defaultMarketBased,
        isFlagEnabled: updatedOrganisationDetails.isFlagEnabled ?? false,
        inflationAdjustmentEnabled: updatedOrganisationDetails.inflationAdjustmentEnabled ?? true,
        consolidationApproach: updatedOrganisationDetails.consolidationApproach ?? 'OPERATIONAL_CONTROL',
      },
      validationSchema: overviewSchema,
      onSubmit: (values) => {
        setEditField('');
        submitOrganisationHandler(values)
      }
    });


    const cancelEditHandler = () => {
      setEditField('');
      if (organisationDetails) {
        formik.setValues({
          name: organisationDetails.name,
          accountingYearEndDate: organisationDetails.accountingYearEndDate || '',
          countryId: organisationDetails.countryId || '',
          sector: organisationDetails.sector || '',
          companyType: organisationDetails.companyType || '',
          assignedUserId: organisationDetails.assignedUserId || '',
          enableProductCarbonFootprint: organisationDetails.enableProductCarbonFootprint || false,
          enableSubsidiaries: organisationDetails.enableSubsidiaries || false,
          enableProjects: organisationDetails.enableProjects || true,
          primaryColor: updatedOrganisationDetails.primaryColor,
          enableRecs: organisationDetails.enableRecs || false,
          currencyCode: organisationDetails.currencyCode || '',
          includeWtt: organisationDetails.includeWtt || false,
          includeTransmissionDistributionLosses: organisationDetails.includeTransmissionDistributionLosses ?? false,
          includeLedgerBills: organisationDetails.includeLedgerBills ?? false,
          defaultMarketBased: organisationDetails.defaultMarketBased || false,
          isFlagEnabled: organisationDetails.isFlagEnabled || false,
          inflationAdjustmentEnabled: organisationDetails.inflationAdjustmentEnabled ?? true,
          consolidationApproach: organisationDetails.consolidationApproach ?? 'OPERATIONAL_CONTROL',
        });
      }
    }

    const selectCountryHandler = (e: SelectChangeEvent<ICountry | null>, value: ICountry | null) => {
      if (value) {
        formik.setValues({ ...formik.values, countryId: value.id })
      }
    }

    // const dateHandler = (date: Moment | null) => {
    //   if (date) {
    //     formik.setValues({ ...formik.values, accountingYearEndDate: date.format('YYYY-MM-DD') })
    //   }
    // }

    const switchScopeThreeHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target;
      organisationService.toggleGroupToOrganisation(name)
        .then(response => {
          userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
          setGroups((prevGroups: IGroup[]) =>
            prevGroups.map((group) =>
              group.id === name ? { ...group, isTagged: checked } : group
            )
          );
        })
        .catch(err => {
          console.log(err)
          if (err.response.data.code === 1) {
            userNotificationsContext.openDialog({
              active: true, title: 'Action Not Allowed',
              message: `This Scope 3 Category cannot be toggled off because there are existing emissions in one or more open report periods. Please review these emissions before proceeding.`,
              buttonCopy: 'Ok',
              hideCancelButton: true
            })
          }
          if (err.response.data.code === 2) {
            userNotificationsContext.openDialog({
              active: true, title: 'Action Not Allowed',
              message: `The Employee Commuting Scope 3 category cannot be toggled off because there are active surveys. Please review your surveys before proceeding.`,
              buttonCopy: 'Ok',
              hideCancelButton: true
            })
          }
          if (err.response.data.code === 3) {
            userNotificationsContext.openDialog({
              active: true, title: 'Action Not Allowed',
              message: `The Upstream Leased Assets Scope 3 category cannot be toggled off because there are active leased locations that are not operationally controlled. Please review your locations and their status before proceeding.`,
              buttonCopy: 'Ok',
              hideCancelButton: true
            })
          }
          if (err.response.data.code === 4) {
            userNotificationsContext.openDialog({
              active: true, title: 'Action Not Allowed',
              message: `The Downstream Leased Assets Scope 3 category cannot be toggled off because there are active leased out locations that are not operationally controlled. Please review your locations and their status before proceeding.`,
              buttonCopy: 'Ok',
              hideCancelButton: true
            })
          }
          if (err.response.data.code === 5) {
            userNotificationsContext.openDialog({
              active: true, title: 'Action Not Allowed',
              message: `The Upstream Leased Assets Scope 3 category cannot be toggled off because there are active leased vehicles. Please review your vehicles and their status before proceeding.`,
              buttonCopy: 'Ok',
              hideCancelButton: true
            })
          }
        })
    }

    const switchProductHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      formik.setFieldValue('enableProductCarbonFootprint', checked)
      submitOrganisationHandler({ ...formik.values, enableProductCarbonFootprint: checked })
    }
    const switchRecsHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      formik.setFieldValue('enableRecs', checked)
      submitOrganisationHandler({ ...formik.values, enableRecs: checked })
    }

    const switchFlagEmissionsHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      formik.setFieldValue('isFlagEnabled', checked)
      submitOrganisationHandler({ ...formik.values, isFlagEnabled: checked })
    }

    const switchDistributionLossesHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      formik.setFieldValue('includeTransmissionDistributionLosses', checked)
      submitOrganisationHandler({ ...formik.values, includeTransmissionDistributionLosses: checked })
      // Switching on only affects records calculated from here, so offer to bring the
      // existing open periods up to date too. The dialog works out whether there is
      // anything to offer and closes itself if not. Switching off needs no prompt: the
      // patch above already stops the existing rows counting.
      if (checked) {
        setCorrectionDialogOpen(true)
      }
    }

    // Both directions move reported totals in every open report period, so the switch stays where
    // it is until the user has seen the change and confirmed it in the dialog.
    const switchIncludeWttHandler = (e: ChangeEvent<HTMLInputElement>) => {
      setWttDialogTarget(e.target.checked)
    }

    // Saved here rather than through submitOrganisationHandler so a refusal (for example a
    // recalculation still running) reaches the dialog with its reason instead of a generic toast.
    const applyIncludeWtt = async (includeWtt: boolean) => {
      await organisationService.editOrganisations({ ...formik.values, includeWtt, confirmIncludeWttChange: true } as IOrganisationDetails)
      formik.setFieldValue('includeWtt', includeWtt)
      setWttDialogTarget(null)
      userDetailsContext.forceUpdateUserDetails()
      userNotificationsContext.openSnackbar({
        open: true,
        message: includeWtt
          ? 'Well-to-tank emissions are now included in your open report periods.'
          : 'Well-to-tank emissions are now excluded from your open report periods.',
      })
    }

    const switchLedgerBillsHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      formik.setFieldValue('includeLedgerBills', checked)
      submitOrganisationHandler({ ...formik.values, includeLedgerBills: checked })
      // Switching on only affects syncs from here, so offer to import the bills earlier
      // syncs set aside. The dialog previews the impact per report period and closes
      // itself if there is nothing waiting. Switching off needs no prompt: bills already
      // committed stay (they are reviewed data), future syncs simply stop importing them.
      if (checked) {
        setLedgerBillsDialogOpen(true)
      }
    }

    const switchInflationAdjustmentHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      formik.setFieldValue('inflationAdjustmentEnabled', checked)
      submitOrganisationHandler({ ...formik.values, inflationAdjustmentEnabled: checked })
    }

    const subsidiaryHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      formik.setFieldValue('enableSubsidiaries', checked)
      submitOrganisationHandler({ ...formik.values, enableSubsidiaries: checked })
    }

    const projectHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      formik.setFieldValue('enableProjects', checked)
      submitOrganisationHandler({ ...formik.values, enableProjects: checked })
    }

    const handleColorChange = (val: string | React.ChangeEvent<HTMLInputElement>) => {
      if (typeof val === 'string') {
        formik.setFieldValue('primaryColor', val);
      } else {
        formik.setFieldValue('primaryColor', val.target.value);
      }
    };

    return (
      <>
        {countries &&
          <Box component='form' onSubmit={formik.handleSubmit}>
            <FormTextInputDisplay
              isEditPage={true}
              editField={editField}
              setEditField={setEditField}
              disabled={editField !== 'name'}
              title='Name'
              name='name'
              value={formik.values.name}
              touched={formik.touched.name}
              errors={formik.errors.name}
              onChange={formik.handleChange}
              cancelEditHandler={cancelEditHandler}
              isTopRow={true}
            />
            {countries &&
              <FormAutoCompleteInputDisplay
                isEditPage={true}
                allowEdit={false}
                editField={editField}
                setEditField={setEditField}
                // disabled={isEditPage && editField !== 'countryId'}
                disabled={true}
                title='Default Country'
                name='countryId'
                value={countries?.find(country => country.id === formik.values.countryId)}
                touched={formik.touched.countryId}
                errors={formik.errors.countryId}
                onChange={(e: any, value: ICountry | null) => selectCountryHandler(e, value)}
                cancelEditHandler={cancelEditHandler}
                options={countries.slice().sort((a, b) => (a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name)))}
                optionLabelKey={'name'}
                optionImage={'imageUrl'}
              />
            }
            {countries && <FormSelectInputDisplay
              isEditPage={true}
              editField={editField}
              setEditField={setEditField}
              disabled={true}
              title='Currency'
              name='currencyCode'
              allowEdit={false}
              value={formik.values.currencyCode}
              touched={formik.touched.currencyCode}
              errors={formik.errors.currencyCode}
              onChange={formik.handleChange}
              cancelEditHandler={cancelEditHandler}
              array={countries
                .filter((country, index, arr) =>
                  arr.findIndex(c => c.currencyCode === country.currencyCode) === index
                )
                .filter((country) => !(country.currencyCode !== 'GBP' && country.currencyCode !== 'USD'))
                .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode))}
              valueKey={'currencyCode'}
              labelKey={(currency) => `${currency.currencyCode} ${currency.currencySymbol ? `(${currency.currencySymbol})` : ''}`}
            />}
            <FormSelectInputDisplay
              isEditPage={true}
              editField={editField}
              setEditField={setEditField}
              disabled={editField !== 'assignedUserId'}
              title='Default Assignee'
              name='assignedUserId'
              value={formik.values.assignedUserId}
              touched={formik.touched.assignedUserId}
              errors={formik.errors.assignedUserId}
              onChange={formik.handleChange}
              cancelEditHandler={cancelEditHandler}
              array={users.sort((a, b) => a.firstName.localeCompare(b.firstName))}
              valueKey={'id'}
              labelKey={(user) => `${user.firstName} ${user.surname}`}
            />
            {/* Legal form for regulation applicability (SECR quoted vs large-unquoted/LLP regimes,
                shown on the Reporting page). The empty option clears it; the BE stores null. */}
            <FormSelectInputDisplay
              isEditPage={true}
              editField={editField}
              setEditField={setEditField}
              disabled={editField !== 'companyType'}
              title='Company Type'
              name='companyType'
              value={formik.values.companyType}
              touched={formik.touched.companyType}
              errors={formik.errors.companyType}
              onChange={formik.handleChange}
              cancelEditHandler={cancelEditHandler}
              array={[
                { value: '', label: 'Not set' },
                { value: 'quoted_company', label: 'Quoted company (listed plc)' },
                { value: 'unquoted_company', label: 'Unquoted company (Ltd / unlisted plc)' },
                { value: 'llp', label: 'Limited liability partnership (LLP)' },
                { value: 'other', label: 'Other (sole trader, partnership, public sector, non-UK)' },
              ]}
              valueKey={'value'}
              labelKey={(option: { value: string, label: string }) => option.label}
            />
            {/* TICKET-C: GHG Protocol consolidation approach (organisational boundary). */}
            <FormSelectInputDisplay
              isEditPage={true}
              editField={editField}
              setEditField={setEditField}
              disabled={editField !== 'consolidationApproach'}
              title='Consolidation Approach'
              name='consolidationApproach'
              value={formik.values.consolidationApproach}
              touched={formik.touched.consolidationApproach}
              errors={formik.errors.consolidationApproach}
              onChange={formik.handleChange}
              cancelEditHandler={cancelEditHandler}
              array={[
                { value: 'OPERATIONAL_CONTROL', label: 'Operational control' },
                { value: 'FINANCIAL_CONTROL', label: 'Financial control' },
                { value: 'EQUITY_SHARE', label: 'Equity share' },
              ]}
              valueKey={'value'}
              labelKey={(o) => o.label}
            />
            <FormColorInputDisplay
              isEditPage={true}
              editField={editField}
              setEditField={setEditField}
              disabled={editField !== 'primaryColor'}
              title={`${screenLabelsContext.getLabel('organisation', { plural: false, capitalize: 'first' })} Color`}
              name='primaryColor'
              value={formik.values.primaryColor}
              touched={formik.touched.primaryColor}
              errors={formik.errors.primaryColor}
              onChange={handleColorChange}
              cancelEditHandler={cancelEditHandler}
            />
            <FormControl fullWidth variant='outlined'>
              <Stack sx={{
                pt: 1, pb: 1, pl: 3,
                borderWidth: '1px',
                borderColor: 'lightGrey.main',
                borderLeftStyle: 'solid',
                borderRightStyle: 'solid',
                borderTopStyle: 'solid',

              }}>
                <Stack position='relative' alignItems={isMobileMode ? 'flex-start' : 'start'} flexDirection={isMobileMode ? 'column' : 'row'} justifyContent='start'>
                  <Typography variant='subtitle1' sx={{ width: '40%', color: 'text.primary', minWidth: '100px', pt: 2 }}>Scope 3 Categories</Typography>
                  <Stack position='relative' flexDirection='column' sx={{ width: '100%' }}>
                    <FormGroup>
                      <Box>
                        < Chip sx={{ mb: 2, mt: 2 }} label={<Typography sx={{ color: 'text.primary' }}>Upstream</Typography>} />
                      </Box>
                      {sortGroupsByGhgCategory(groups.filter(group => group.scope === '3' && group.direction === 'Upstream')).map((group) => {
                        return (
                          < FormControlLabel control={< Switch checked={group.isTagged} onChange={switchScopeThreeHandler} name={group.id} />} label={
                            <Stack display='flex' direction='row' alignItems='center'>
                              {formatGroupLabel(group, { includeScope: false })}
                              <TooltipHover
                                title={group.title}
                                backgroundColor='white'
                                text={getDescription(group.title)}
                                placement='right'><InfoOutlined sx={{ ml: 1, color: 'text.secondary' }} /></TooltipHover>
                            </Stack>} key={group.title}
                          />)
                      })}
                      <Box>
                        < Chip sx={{ mb: 2, mt: 2 }} label={<Typography sx={{ color: 'text.primary' }}>Downstream</Typography>} />
                      </Box>
                      {sortGroupsByGhgCategory(groups.filter(group => group.scope === '3' && group.direction === 'Downstream')).map((group) => {
                        return (
                          < FormControlLabel control={< Switch checked={group.isTagged} onChange={switchScopeThreeHandler} name={group.id} />} label={
                            <Stack display='flex' direction='row' alignItems='center'>
                              {formatGroupLabel(group, { includeScope: false })}
                              <TooltipHover
                                title={group.title}
                                backgroundColor='white'
                                text={getDescription(group.title)}
                                placement='right'><InfoOutlined sx={{ ml: 1, color: 'text.secondary' }} /></TooltipHover>
                            </Stack>
                          } key={group.title} />
                        )
                      })}
                    </FormGroup>
                  </Stack>
                </Stack>
              </Stack>
            </FormControl>
            <FormToggleInputDisplay
              isEditPage={false}
              editField={editField}
              setEditField={setEditField}
              disabled={subsidiaries.length > 1}
              title={screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' })}
              name='enableSubsidiaries'
              value={formik.values.enableSubsidiaries || false}
              onChange={subsidiaryHandler}
              tooltip={`Enable this feature to add ${screenLabelsContext.getLabel('subsidiary', { plural: true })} to your organisation`}
            />
            <FormToggleInputDisplay
              isEditPage={false}
              editField={editField}
              setEditField={setEditField}
              disabled={false}
              title={screenLabelsContext.getLabel('project', { plural: true, capitalize: 'first' })}
              name='enableProjects'
              value={formik.values.enableProjects || false}
              onChange={projectHandler}
              tooltip={`Enable this feature to add ${screenLabelsContext.getLabel('project', { plural: true })} to your organisation`}
            />
            <FormToggleInputDisplay
              isEditPage={false}
              editField={editField}
              setEditField={setEditField}
              disabled={false}
              title='Renewable Energy Certificates'
              name='enableRecs'
              value={formik.values.enableRecs || false}
              onChange={switchRecsHandler}
              tooltip={`Enable this feature to add Renewable Energy Certificates (e.g. RECs, REGOs, GoOs) to your organisation`}
            />
            <FormToggleInputDisplay
              isEditPage={false}
              editField={editField}
              setEditField={setEditField}
              disabled={false}
              title='FLAG Emissions'
              name='isFlagEnabled'
              value={formik.values.isFlagEnabled || false}
              onChange={switchFlagEmissionsHandler}
              tooltip='Enable FLAG-related emissions tracking, reporting and target setting for this organisation.'
            />
            <FormToggleInputDisplay
              isEditPage={false}
              editField={editField}
              setEditField={setEditField}
              disabled={false}
              isBottomRow={false}
              title='Product Carbon Footprint'
              name='enableProductCarbonFootprint'
              value={formik.values.enableProductCarbonFootprint || false}
              onChange={switchProductHandler}
              tooltip='Enable this feature to calculate the emissions involved in producing your products.'
            />
            <FormRadioInputDisplay
              isEditPage={false}
              allowEdit={false}
              row={true}
              editField={editField}
              setEditField={setEditField}
              disabled={true}
              isBottomRow={false}
              title='Default for Emission Calculations'
              name='defaultMarketBased'
              value={formik.values.defaultMarketBased ? 'market' : 'location'}
              touched={formik.touched.defaultMarketBased}
              errors={formik.errors.defaultMarketBased}
              onChange={() => {}}
              optionOneLabel='Market Based'
              optionOneValue='market'
              optionTwoLabel='Location Based'
              optionTwoValue='location'
            />
            <FormToggleInputDisplay
              isEditPage={false}
              editField={editField}
              setEditField={setEditField}
              disabled={false}
              isBottomRow={false}
              title='Include WTT Emission Calculations'
              name='includeWtt'
              value={formik.values.includeWtt || false}
              onChange={switchIncludeWttHandler}
              tooltip='Well-to-tank emissions are the upstream emissions from extracting, refining and transporting fuels, and from producing the electricity and heat you buy. They are reported in Scope 3, mostly in Category 3. Changing this setting updates your open report periods straight away, after you have seen and confirmed the change. Locked periods are never changed.'
            />
            <FormToggleInputDisplay
              isEditPage={false}
              editField={editField}
              setEditField={setEditField}
              disabled={false}
              isBottomRow={false}
              title='Include Transmission & Distribution Losses'
              name='includeTransmissionDistributionLosses'
              value={formik.values.includeTransmissionDistributionLosses ?? false}
              onChange={switchDistributionLossesHandler}
              tooltip='Some of the electricity generated for you is lost in the grid before it reaches your meter, and district heat loses energy travelling through the network. The GHG Protocol counts those losses in Scope 3 Category 3, fuel and energy related activities. This setting also files the upstream fuel emissions of vehicles you operate under Category 3 rather than Business Travel or Upstream Transportation, which is where the Protocol assigns them. Records already entered keep their current treatment until they are next edited, and periods you have locked are never affected.'
            />
            <FormToggleInputDisplay
              isEditPage={false}
              editField={editField}
              setEditField={setEditField}
              disabled={false}
              isBottomRow={false}
              title='Import Supplier Bills from Accounting Syncs'
              name='includeLedgerBills'
              value={formik.values.includeLedgerBills ?? false}
              onChange={switchLedgerBillsHandler}
              tooltip='Your accounting syncs always bring in direct bank payments. Most supplier spend arrives as bills paid later, so with this off part of your ledger is not counted. Turning it on imports supplier bills and purchase credit notes as well, with safeguards so the same money is never counted as both a bill and its payment. You will see the impact on each report period and confirm before any earlier bills are imported.'
            />
            <FormToggleInputDisplay
              isEditPage={false}
              editField={editField}
              setEditField={setEditField}
              disabled={false}
              isBottomRow={true}
              title='Apply Inflation Adjustment to Spend-based Emissions'
              name='inflationAdjustmentEnabled'
              value={formik.values.inflationAdjustmentEnabled ?? true}
              onChange={switchInflationAdjustmentHandler}
              tooltip='View the inflation index values used for spend-based calculations'
              onInfoClick={() => navigate('/settings/company-settings/inflation-indices')}
            />
          </Box>
        }
        <DistributionLossCorrectionDialog
          open={correctionDialogOpen}
          onClose={() => setCorrectionDialogOpen(false)}
        />
        <LedgerCoverageCorrectionDialog
          open={ledgerBillsDialogOpen}
          onClose={() => setLedgerBillsDialogOpen(false)}
        />
        <WttPolicyDialog
          open={wttDialogTarget !== null}
          includeWtt={wttDialogTarget ?? true}
          onClose={() => setWttDialogTarget(null)}
          onConfirm={() => applyIncludeWtt(wttDialogTarget ?? true)}
        />
      </>
    )
  }